import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { Box, Typography, IconButton, Tooltip, Skeleton } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import SettingsIcon from '@mui/icons-material/Settings'
import { useTheme } from '@/contexts/ThemeContext'
import { useTreasuryBalance } from '@/contexts/TreasuryBalanceContext'
import { useNotification } from '@/hooks/useNotification'
import { useWallet } from '@/hooks/useWallet'
import { getEnv } from '@/config/env'
import STNGImplementationAbi from '@/abis/STNGImplementation.json'
import { usePublicClient } from 'wagmi'
import type { Abi } from 'viem'
import { formatTokenBalance } from '@/utils/formatting'
import { copyToClipboard } from '@/utils/clipboard'
import {
  SKELETON_WIDTH_MEDIUM,
  POLLING_INTERVAL_BALANCE,
} from '@/constants/main'
import {
  createCardStyles,
  createSecondaryTextStyles,
} from '@/components/shared/commonStyles'
import SectionHeader from '@/components/shared/SectionHeader'
import SafeSettingsModal from './SafeSettingsModal'
import { useChainId } from 'wagmi'
import SafeAbi from '@/abis/Safe.json'
import { getAddress, createPublicClient, http } from 'viem'
import { mainnet, sepolia } from 'wagmi/chains'

export default function AccountInfo() {
  const { formatAddress } = useWallet()
  const { showSuccess } = useNotification()
  const { colors } = useTheme()
  const { setTreasuryBalance } = useTreasuryBalance()

  const [tokenBalance, setTokenBalance] = useState<string>('—')
  const [tokenSymbol, setTokenSymbol] = useState<string>('-')
  const [isLoadingToken, setIsLoadingToken] = useState<boolean>(false)
  const [owners, setOwners] = useState<string[]>([])
  const [threshold, setThreshold] = useState<number | null>(null)
  const [settingsModalOpen, setSettingsModalOpen] = useState(false)
  const isFirstLoad = useRef(true)

  const { treasurySafe, stngProxy, mainnetRpcUrl, sepoliaRpcUrl } = useMemo(
    () => getEnv(),
    []
  )
  const publicClient = usePublicClient()
  const chainId = useChainId()

  useEffect(() => {
    if (!publicClient || !treasurySafe) return

    const fetchBalance = async () => {
      try {
        if (isFirstLoad.current) {
          setIsLoadingToken(true)
          isFirstLoad.current = false
        }
        const [rawBalance, decimals, symbol] = await Promise.all([
          publicClient.readContract({
            address: stngProxy as `0x${string}`,
            abi: STNGImplementationAbi as Abi,
            functionName: 'balanceOf',
            args: [treasurySafe as `0x${string}`],
          }) as Promise<bigint>,
          publicClient.readContract({
            address: stngProxy as `0x${string}`,
            abi: STNGImplementationAbi as Abi,
            functionName: 'decimals',
          }) as Promise<number>,
          publicClient.readContract({
            address: stngProxy as `0x${string}`,
            abi: STNGImplementationAbi as Abi,
            functionName: 'symbol',
          }) as Promise<string>,
        ])
        const formattedBalance = formatTokenBalance(
          rawBalance,
          decimals,
          symbol
        )
        setTokenSymbol(symbol)
        setTreasuryBalance(rawBalance)
        setTokenBalance((prev) => {
          if (prev === formattedBalance) {
            return prev
          }
          return formattedBalance
        })
      } catch {
        setTokenBalance((prev) => {
          if (prev === '—') {
            return prev
          }
          return '—'
        })
      } finally {
        setIsLoadingToken(false)
      }
    }

    fetchBalance()

    const interval = setInterval(fetchBalance, POLLING_INTERVAL_BALANCE)

    return () => {
      clearInterval(interval)
    }
  }, [publicClient, treasurySafe, stngProxy, setTreasuryBalance])

  const fetchTreasurySafeInfo = useCallback(async () => {
    if (!publicClient || !treasurySafe || !chainId) return

    try {
      let rpcUrl: string | undefined
      let chain: typeof mainnet | typeof sepolia

      if (chainId === mainnet.id) {
        rpcUrl = mainnetRpcUrl || mainnet.rpcUrls.default.http[0]
        chain = mainnet
      } else if (chainId === sepolia.id) {
        rpcUrl = sepoliaRpcUrl || sepolia.rpcUrls.default.http[0]
        chain = sepolia
      } else {
        return
      }

      if (!rpcUrl) return

      const chainPublicClient = createPublicClient({
        chain,
        transport: http(rpcUrl),
      })

      const [ownersResult, thresholdResult] = await Promise.all([
        chainPublicClient.readContract({
          address: treasurySafe as `0x${string}`,
          abi: SafeAbi as Abi,
          functionName: 'getOwners',
        }) as Promise<readonly `0x${string}`[]>,
        chainPublicClient.readContract({
          address: treasurySafe as `0x${string}`,
          abi: SafeAbi as Abi,
          functionName: 'getThreshold',
        }) as Promise<bigint>,
      ])

      setOwners(ownersResult.map((addr) => getAddress(addr)))
      setThreshold(Number(thresholdResult))
    } catch (error) {
      console.error('Failed to fetch treasury Safe info:', error)
    }
  }, [publicClient, treasurySafe, chainId, mainnetRpcUrl, sepoliaRpcUrl])

  useEffect(() => {
    fetchTreasurySafeInfo()
  }, [fetchTreasurySafeInfo])

  useEffect(() => {
    if (settingsModalOpen) {
      fetchTreasurySafeInfo()
    }
  }, [settingsModalOpen, fetchTreasurySafeInfo])

  const handleCopy = useCallback(async () => {
    try {
      if (!treasurySafe) return
      await copyToClipboard(treasurySafe)
      showSuccess('Address copied to clipboard')
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }, [treasurySafe, showSuccess])

  return (
    <Box sx={{ mb: 4 }}>
      <SectionHeader
        title="Treasury Account Info"
        action={
          <Tooltip title="Settings">
            <IconButton
              onClick={() => setSettingsModalOpen(true)}
              sx={{
                color: colors.primary,
                '&:hover': {
                  backgroundColor: `${colors.primary}22`,
                },
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        }
      />
      <Box sx={createCardStyles(colors)}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ ...createSecondaryTextStyles(colors), mb: 0.5 }}
          >
            Address
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body1"
              sx={{
                color: colors.text,
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {treasurySafe || '—'}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.text,
                fontWeight: 500,
                display: { xs: 'block', sm: 'none' },
              }}
            >
              {treasurySafe ? formatAddress(treasurySafe) : '—'}
            </Typography>
            <Tooltip title="Copy address">
              <IconButton
                onClick={handleCopy}
                size="small"
                sx={{
                  color: colors.primary,
                  '&:hover': {
                    backgroundColor: `${colors.primary}22`,
                  },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="body2"
            sx={{ ...createSecondaryTextStyles(colors), mb: 0.5 }}
          >
            {tokenSymbol} Balance
          </Typography>
          {isLoadingToken ? (
            <Skeleton
              variant="text"
              width={SKELETON_WIDTH_MEDIUM}
              sx={{ bgcolor: `${colors.primary}33` }}
            />
          ) : (
            <Typography
              variant="body1"
              sx={{ color: colors.text, fontWeight: 500 }}
            >
              {tokenBalance}
            </Typography>
          )}
        </Box>
      </Box>
      {treasurySafe && (
        <SafeSettingsModal
          open={settingsModalOpen}
          onClose={() => setSettingsModalOpen(false)}
          safeAddress={treasurySafe}
          currentOwners={owners}
          currentThreshold={threshold !== null ? threshold : 1}
          chainId={String(chainId)}
          onSuccess={() => {
            fetchTreasurySafeInfo()
          }}
        />
      )}
    </Box>
  )
}
